import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box
} from '@mui/material';
import axios from 'axios';
import configuration from '../config';
import Layout from '../components/layout/InvoiceLayout.jsx';
import { useSelector } from 'react-redux';

const PurchaseHistory = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = configuration;

  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchPurchases();
    } else {
      setLoading(false);
    }
  }, [selectedCompanyId]); // Added dependency to re-fetch when company changes

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getInvoiceBykyc/${selectedCompanyId}`
      );
      
      // Handle the response data properly
      let invoicesData = response.data;
      
      // Check if it's an array or if we need to extract it
      if (!Array.isArray(invoicesData)) {
        if (invoicesData && typeof invoicesData === 'object') {
          // If it's an object with a data property that contains the array
          if (Array.isArray(invoicesData.data)) {
            invoicesData = invoicesData.data;
          } else {
            // If it's just a single object, wrap it in an array
            invoicesData = [invoicesData];
          }
        } else {
          // Fallback to empty array
          invoicesData = [];
          console.warn("Received unexpected data format:", response.data);
        }
      }
      
      // Extract purchases safely with fallbacks for missing properties
      const allPurchases = extractPurchases(invoicesData);
      setPurchases(allPurchases);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching purchase history:", err);
      setError("Failed to fetch purchase history. Please try again later.");
      setLoading(false);
    }
  };

  const extractPurchases = (invoices) => {
    // Make sure we have an array and safely extract purchases
    if (!Array.isArray(invoices)) {
      console.warn("Expected invoices array, received:", typeof invoices);
      return [];
    }
    
    // Use reduce instead of flatMap for better compatibility
    return invoices.reduce((acc, invoice) => {
      // Check if invoice has items property and it's an array
      const items = invoice.items && Array.isArray(invoice.items) 
        ? invoice.items 
        : [];
      
      const invoiceItems = items.map(item => ({
        invoiceId: invoice.invoiceId || 'N/A',
        date: invoice.date 
          ? new Date(invoice.date).toLocaleDateString() 
          : 'N/A',
        productName: item.description || 'Unknown Product',
        quantity: item.qty || 0,
        unitPrice: item.rate || 0,
        total: (item.qty || 0) * (item.rate || 0),
      }));
      
      return [...acc, ...invoiceItems];
    }, []);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Purchase History
        </Typography>
        
        {purchases.length === 0 ? (
          <Typography variant="body1">No purchase history found.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="purchase history table">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price (₹)</TableCell>
                  <TableCell align="right">Total (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.map((purchase, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {purchase.invoiceId}
                    </TableCell>
                    <TableCell>{purchase.date}</TableCell>
                    <TableCell>{purchase.productName}</TableCell>
                    <TableCell align="right">{purchase.quantity}</TableCell>
                    <TableCell align="right">{purchase.unitPrice.toFixed(2)}</TableCell>
                    <TableCell align="right">{purchase.total.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Layout>
  );
};

export default PurchaseHistory;